export const projects = [
  {
    id: 15,
    title: "Wizehire",
    short_desc:
      "Built and shipped high-impact hiring, assessment, onboarding, payments, and data-platform features across Wizehire's Django and React stack.",
    tech: [
      "Python",
      "Django",
      "React",
      "LangChain",
      "Crew AI",
      "LLMs",
      "Ollama",
      "Gemini",
      "Rust",
      "Kubernetes",
      "ArgoCD",
      "Terraform",
      "AWS",
      "GitOps",
      "Statsig",
      "OAuth",
    ],
    lists: {
      "Senior Software Engineer (Mar 2024 - Present)": [
        "Built an agentic AI interviewing application using Crew AI, LangChain, and LLMs like Ollama and Gemini to generate personalized technical assessments, adapt difficulty in real time, execute test cases, and deliver candidate feedback and scoring.",
        "Designed and shipped an AI-powered resume parsing feature that extracts contextual insights and dynamically generates assessment questions tailored to each applicant's background.",
        "Developed a secure end-to-end finance ETL pipeline using Kubernetes, GitOps, ArgoCD, Terraform, and AWS services.",
        "Led a team of 3 engineers to streamline onboarding and payments, increasing paid signups by 68% and ARR by 40%, while also driving acquisition through a free-trial flow, Google and LinkedIn OAuth, and a Statsig-based A/B testing framework.",
        "Built API integrations for HR and payroll tools including QuickBooks, Rippling, and Paylocity.",
      ],
      "Software Engineer (Aug 2022 - Mar 2024)": [
        "Developed the end-to-end job application flow and jobs search engine with Django and React, supporting more than 100k applicants per week.",
        "Built a Python ETL pipeline and Rust-based tracking tools for LinkedIn and Indeed integrations, using ArgoCD and Kubernetes to reduce integration failure incidents by more than 90% and improve service reliability and monitoring.",
      ],
    },
    desc: `At Wizehire, I worked across product, platform, and growth initiatives spanning hiring workflows, AI-powered candidate evaluation, finance infrastructure, and third-party integrations. My work ranged from building applicant-facing job search experiences used at scale to leading newer AI and payments initiatives that directly improved conversion, revenue, and operational efficiency.

Over time, I contributed as both a Software Engineer and Senior Software Engineer, partnering closely with product and engineering teams to deliver reliable full-stack features and production-ready infrastructure. The projects combined Django and React application development with cloud-native tooling, experimentation systems, and integrations across hiring, payroll, and accounting platforms.`,
    images: ["/projects/Wizehire/Wizehire.jpg"],
    link: "https://wizehire.com",
  },
  {
    id: 14,
    title: "Dutrow Technologies",
    short_desc:
      "Built the core full-stack ordering, CRM, and operations platform for a cloud kitchen startup as a solo developer.",
    tech: [
      "Full-Stack Development",
      "CRM Systems",
      "Twilio",
      "Google Maps",
      "GoHighLevel",
      "DevOps",
      "SEO",
      "UI/UX",
    ],
    lists: {
      "Full-Stack Developer (Sep 2020 - Mar 2022)": [
        "Worked as the solo developer at a cloud kitchen startup, owning both application development and DevOps responsibilities.",
        "Built a fully fledged internal CRM with integrations for Twilio, Google Maps, and GoHighLevel.",
        "Developed a food delivery and online ordering platform that processed more than 1k orders per day.",
        "Implemented the company website with a refined UI/UX and stronger SEO, contributing to a 120% increase in revenue.",
      ],
    },
    desc: `Dutrow Technologies was a fast-moving cloud kitchen startup where I owned the engineering stack end to end. I built internal operations tooling, customer-facing ordering experiences, and the surrounding integrations needed to support sales, delivery workflows, and day-to-day business operations.

This role gave me strong ownership across product development and infrastructure, from shipping the CRM and ordering platform to improving the public website experience and search visibility. The work focused heavily on practical business impact, reliability, and speed of execution in a startup environment.`,
    images: ["/projects/Dutrow/Home.png", "/projects/Dutrow/Ordering.png"],
    link: "https://alekitchen.com",
  },
  {
    id: 13,
    title: "Lockbox",
    short_desc:
      "Lockbox is a command-line tool for generating and managing passwords. It uses strong encryption algorithms to securely store your passwords, so you can be sure that your data is safe.",
    tech: ["Rust", "Clap-CLI", "AES-GCM Encryption", "Serde"],
    lists: {
      "Project Features": [
        "Secure: Lockbox uses the cutting-edge AES-GCM (Advanced Encryption Standard with Galoise Counter Mode) encryption algorithm to ensure that your passwords are always safe and secure. You can rest easy knowing that your data is protected by the best.",
        "One Master Key: With Lockbox, you only need to remember one master password. The advanced PBKDF2 (Password-Based Key Derivation Function 2) key derivation function takes care of the rest, allowing you to access all your passwords with ease.",
        "Command-Line Power: Lockbox comes with a fully functional command-line interface (CLI) and a Read-Eval-Print Loop (REPL), giving you complete control over your password management through the terminal.",
        "Tested and Verified: Lockbox’s codebase is thoroughly tested and verified, with code coverage reports available for all to see. You can trust that Lockbox is reliable and dependable. For more details on lock box test coverage head over to: https://app.codecov.io/gh/SonuBardai/lockbox",
      ],
    },
    desc: `Introducing Lockbox, the ultimate command-line tool for generating and managing passwords. Built with the fast and memory-safe Rust language, Lockbox is designed to keep your passwords safe and secure.

With its cutting-edge AES-GCM encryption algorithm, Lockbox ensures that your data is always protected by the best. And with its advanced PBKDF2 key derivation function, you only need to remember one master password to access all your passwords with ease.

But that's not all. Lockbox also comes with a fully functional command-line interface (CLI) and a Read-Eval-Print Loop (REPL), giving you complete control over your password management through the terminal. And with its thoroughly tested and verified codebase, you can trust that Lockbox is reliable and dependable.

But what really sets Lockbox apart is its commitment to the open-source community. Created to help beginners who are learning Rust to contribute to open-source code, Lockbox is more than just a password manager - it's a learning tool. With detailed YouTube videos explaining how to write Rust code, Lockbox is the perfect project for anyone looking to get started with open-source development.
`,
    images: ["/projects/Lockbox/lockbox-1.jpg"],
    link: "https://github.com/SonuBardai/lockbox",
    youtube:
      "https://www.youtube.com/embed/videoseries?list=PLzf64od8-Qx5dJ3DESnk_suQL9cu4fBn5",
  },
  {
    id: 12,
    title: "Alacritty",
    short_desc:
      "Active contributor of the open source project Alacritty, a cross-platform OpenGL terminal emulator made in Rust.",
    tech: ["Rust"],
    lists: {
      "Some of my contributions include:": [
        "Implementing a feature to allow hints to persist after selection, as described in issue [#6976]. This enhancement allows users to continue interacting with hints even after making a selection, improving the usability of the hints feature.",
        "Removing the mouse double_click and triple_click options as part of issue [#6962]. This change simplifies the configuration options for mouse interactions, making it easier for users to customize their Alacritty experience.",
        "Addressing an issue where the colors.transparent_background_colors option did not apply to selections, as reported in issue [#6859]. By fixing this bug, I helped ensure that Alacritty’s color options work as expected, providing users with greater control over the appearance of their terminal.",
      ],
    },
    desc: "As a contributor to the open-source project Alacritty, I have worked on several issues to improve the functionality and user experience of the terminal emulator. I have helped improve the Alacritty project and make it an even better tool for its users. I am proud to be a part of this vibrant open-source community and look forward to continuing to contribute to its development.",
    images: ["/projects/Alacritty/img-1.png"],
    link: "https://github.com/alacritty/alacritty/commits?author=SonuBardai",
  },
  {
    id: 11,
    title: "SpeechAI chat app",
    short_desc:
      "A configurable AI chat app that integrated with the OpenAI API and ElevenLabs API to allow users to chat with the AI chatbot and receive spoken responses from the AI",
    tech: [
      "Django",
      "ReactJS",
      "Python",
      "TypeScript",
      "OpenAI API",
      "ElevenLabs API",
    ],
    lists: {
      // "Project Features": [],
    },
    desc: "Worked on several feature issues.",
    images: ["/projects/SpeechAI/img.png"],
    link: "https://github.com/SonuBardai/speech_ai",
  },
  {
    id: 10,
    title: "GoLang Chatapp",
    short_desc:
      "A full-stack react-golang chat application implemented using Golang Websockets and Typescript React JS. A complete Youtube series was made to guide beginners through the process of developing the project.",
    tech: ["GoLang", "ReactJS", "TypeScript", "golang.org/x/net/websocket"],
    lists: {
      // "Project Features": [""],
    },
    desc: `This full-stack chat application was developed using React JS and Golang, with the front-end implemented in Typescript and the back-end using Golang Websockets. This project provides a seamless chatting experience, allowing users to communicate in real-time in a group chat interface.

The front-end of the application was built using React JS, a popular JavaScript library for building user interfaces. The use of Typescript, a typed superset of JavaScript, adds additional type checking and improved code readability. The back-end was implemented using Golang, a statically-typed programming language known for its efficiency and concurrency. Websockets were used to enable real-time communication between the client and server.

To guide beginners through the process of developing this project, a complete Youtube series was created. The series covers everything from setting up the development environment to implementing the various features of the chat application. This is an excellent resource for anyone looking to learn more about full-stack development using React JS and Golang.`,
    images: ["/projects/GolangWS/img.png"],
    link: "https://github.com/SonuBardai/golang_chat_app",
    youtube:
      "https://www.youtube.com/embed/videoseries?list=PLzf64od8-Qx534U2yOW6IK-IZJUA0QpVu",
  },
  {
    id: 9,
    title: "Pals - A Full-Stack Social Media Application",
    short_desc:
      "A complete full-stack social media application developed using the MERN stack.",
    tech: [
      "React JS",
      "Node JS",
      "Express JS",
      "MongoDB",
      "JSON Web Tokens (JWT)",
      "Vanilla CSS",
      "JS",
    ],
    lists: {
      "Project Features": [
        "This social media application includes all the commonly-used social media functionalities, including",
        "Login, Register, and Logout.",
        "Sharing Posts and Images.",
        "Liking and Commenting on Posts and using Hashtags to group posts.",
        "Refine posts on the page using different filters, such as filter posts by popularity, filter by hashtags, filter posts to only see those by people that I follow, filter posts that only have media, filter posts that I have replied to, etc.",
        "Following and Unfollowing users.",
        "Searching for users.",
        "Light and Dark themes.",
        "And more.",
      ],
    },
    desc: "Pals is a complete social media application created utilising best practises for React and Express. The app has a sleek and contemporary user experience in addition to all the frequently used social media capabilities. Pals is a brilliant platform that taught me a lot about the architecture and functioning of social media applications. I built this project from scratch, using React and Express best practices. This project also gave me a great experience on building modern and elegant user interface that are easy to use as well as aesthetically pleasing. ",
    images: [
      "/projects/Pals/img2.png",
      "/projects/Pals/img1.png",
      "/projects/Pals/img3.png",
      "/projects/Pals/img4.png",
      "/projects/Pals/img5.png",
    ],
    link: "https://github.com/SonuBardai/Pals-MERN-App",
  },
  {
    id: 8,
    title: "RealEst - An Azure Cloud based Real Estate Marketplace",
    short_desc:
      "RealEst is a full-stack Real Estate website powered by Azure's cloud services.",
    tech: [
      "HTML",
      "CSS",
      "TailwindCSS",
      "JS",
      "Python Django",
      "Azure Computer Vision",
      "Azure Postgres Database Server",
      "Azure Blob Storage Account",
      "Azure Web App for Hosting",
    ],
    lists: {
      "Azure Services Used": [
        "Azure Postgres Database Server: This Azure service is used to store the user data and the data related to the properties that are listed on the application dashboard.",
        "Azure Blob Storage Account: This Azure service is used to store all the static files associated with the web application, and also to store the property images uploaded while listing the properties.",
        "Azure Computer Vision: This Azure service is used to generate metadata tags associated with the property images.",
        "Everytime a seller lists a property, the images associated with the property are sent to the Azure Computer Vision resource in a REST API call.",
        "The API returns a set of tags that describe the images, which are then stored along with the property listing.",
        "These tags assist the buyers in filtering out properties and finding property listings with specific features.",
      ],
    },
    desc: `RealEst is an Azure-driven real estate marketplace, that uses Azure services such as Azure Web Apps, Azure Postgres Database Server, Azure Blob Storage Account, and Azure Computer Vision. This is a project created for Microsoft's Future Ready Talent Internship Program.

        The problem with the current state of the online real estate market is that it is frustrating for property buyers to find the perfect house that they desire, and for property sellers to give a concise and comprehensive description of their house that would make their listing visible to the appropriate customers.
        
        RealEst attempts to solve this issue by making use of Azure's Computer Vision technology to help real estate sellers automatically create appropriate tags and filters for their listings, and for buyers to find houses with the exact characteristics that they desire.
        `,
    images: [
      "/projects/RealEst/Screenshot(1).png",
      "/projects/RealEst/Screenshot(2).png",
      "/projects/RealEst/Screenshot(3).png",
      "/projects/RealEst/Screenshot(4).png",
    ],
    link: "https://github.com/SonuBardai/Azure-RealEst-WebApp",
    youtube: "https://www.youtube.com/embed/1VdZL36kXBw",
  },
  {
    id: 7,
    title: "Online Markdown Editing Application",
    short_desc:
      "This is a fully functional Markdown editor created using MERN.",
    tech: ["React JS", "Node JS", "Express JS", "MongoDB", "Vanilla CSS", "JS"],
    desc: `This project is a markdown editing application created using MERN (MongoDB, ExpressJS, ReactJS, and NodeJS).
        It has a server API that performs all CRUD operations on the markdown notes stored in the DB.
        The front-end has a light and dark theme option.
        The front-end has a fully functional multiple-tab option that can be used to simultaneously work on different markdown files.`,
    images: [
      "/projects/Markdown/demolight-1.png",
      "/projects/Markdown/demodark-1.png",
    ],
    link: "https://github.com/SonuBardai/MERN-Markdown-Editor",
  },
  {
    id: 6,
    title: "ProgrammingCorner - A Complete Programmer's Toolkit",
    short_desc:
      "A real-time chatting and online-compiler website made using Node JS.",
    tech: [
      "HTML",
      "CSS",
      "TailwindCSS",
      "JS",
      "Node JS",
      "PassportJS",
      "Express JS",
      "SOCKET.IO",
      "MongoDB",
    ],
    lists: {
      "Project Modules": [
        "Resume Builder",
        "Group Chat",
        "Blogging",
        "Online Code Compiler",
        "Authentication System",
      ],
    },
    desc: `Programming Corner is a hub for programmers to find solutions to their programming needs.
        It is a full-stack web application that provides a community for programmers to share and learn different subjects of Information Technology.
        Our web-based interactive platform aids coders and tech-enthusiasts to teach others and create an environment for themselves to improve their technical abilities.
        It is a one-stop center for programming aficionados to access tech solutions and overcome all the hurdles that they come across in their coding journeys.
        Programming Corner provides a wide range of modules that facilitate coders to boost their programming skills.
        The range of services provided by Programming Corner is Resume Building, Group Chatting, Blogging, Online Code Compiling, and an Authentication System. 
        `,
    images: [
      "/projects/Major/img1.png",
      "/projects/Major/img2.png",
      "/projects/Major/img3.png",
      "/projects/Major/img4.png",
      "/projects/Major/img5.png",
      "/projects/Major/img6.png",
    ],
    link: "https://github.com/SonuBardai/Programming-Corner-MajorProject",
  },
  {
    id: 5,
    title: "TravelR - A Full-Stack Travel Journal",
    short_desc:
      "A Travelling Journal Website made in NodeJS, ExpressJS, and MongoDB.",
    tech: [
      "HTML",
      "Bootstrap",
      "JS",
      "Node JS",
      "Express JS",
      "MongoDB",
      "PassportJS",
    ],
    desc: "A Travelling Journal Website made in NodeJS, ExpressJS, and MongoDB with beautiful Bootstrap templates and PassportJS authentication.",
    images: [
      "/projects/Travel/image1.png",
      "/projects/Travel/image2.png",
      "/projects/Travel/image3.png",
    ],
    link: "https://github.com/SonuBardai/TravelR-NodeJS-ExpressJS-MongoDB",
  },
  {
    id: 4,
    title: "AudioMonkey - An Audio Equipment Marketplace",
    short_desc:
      "A full-stack audio equipment website made using Python Django.",
    lists: {
      "Project Features": [
        "This project has a seperate UI for an admin and a regular user. The admin login allows the user to add, edit, and delete items in the shop.",
        "The regular user login is a buying entity that can surf the web page and place orders.",
        "For the convenience of online shopping, the user can filter the products based on the categories: Headphones, Speakers, Microphones, Earbuds.",
        "It provides the user with a list of the various products available for purchase in the store.",
        "A completely functioning full-stack shopping cart is provided to the user.",
        "The cart saves the state of the cart in the database everytime the user interacts with the products in the cart.",
        "This makes the application easily portable on different platforms, preserving the user's choices.",
      ],
    },
    tech: ["HTML", "CSS", "JS", "Python Django", "SQLite"],
    desc: "A full-stack audio equipment website made using Python Django. It gives the user a list of all the different goods that are sold in the store. For the convenience of online shopping, the user can filter the products based on the categories: Headphones, Speakers, Microphones, Earbuds. The user is given a fully functional full-stack shopping cart that saves the cart's state in the database every time the user interacts with the products in the cart. This makes the application portable across platforms while preserving the user's preferences. After checking out, the products are sent to a checkout page. The system is implemented using Python's web framework Django.",
    images: ["/projects/Audio/img1.png", "/projects/Audio/img2.png"],
    link: "https://github.com/SonuBardai/AudioPhile-Django-Project",
  },
  {
    id: 3,
    title: "Django-React Notes App",
    short_desc:
      "A Note taking application developed using a React and Django stack.",
    tech: ["Python Django", "TailwindCSS", "React JS"],
    desc: "A Note taking application developed using a React and Django stack.",
    images: ["/projects/Notes/img1.png", "/projects/Notes/img2.png"],
    link: "https://github.com/SonuBardai/Django-React-NotesApp",
  },
  {
    id: 2,
    title: "Clothing Store Website",
    short_desc: "A Clothing store webpage Front-End made with ReactJS.",
    tech: ["ReactJS", "TailwindCSS", "CSS", "JS"],
    desc: "A Clothing store webpage Front-End made with ReactJS, with a functioning cart and product listing page.",
    images: [
      "/projects/Shopping/img2.png",
      "/projects/Shopping/img1.png",
      "/projects/Shopping/img3.png",
    ],
    link: "https://github.com/SonuBardai/React-Shopping-Cart",
  },
  {
    id: 1,
    title: "Blogging Website implementing Natural Language Processing",
    short_desc:
      "A full-stack blogging application implemented in Flask Python.",
    tech: [
      "Python Flask",
      "MySQL",
      "HTML",
      "CSS",
      "Javascript",
      "Natural Language Toolkit (NLTK)",
    ],
    desc: "This is a full-stack blog application implemented in Flask Python. It implements Sentiment analysis using Natural Language Processing in NLTK Vader Sentiment Analyzer. An emoji is displayed corresponding to the sentiment that is detected in each blog post.",
    images: ["/projects/Blog/img1.jpg"],
    link: "https://github.com/SonuBardai/Blog-application-with-Sentiment-Analysis",
  },
  {
    id: 0,
    title: "Sudoku App That Solves Itself Using Backtracking",
    short_desc:
      "A Sudoku game based on Python's PyGame GUI and a Backtracking algorithm.",
    tech: ["Python", "PyGame"],
    lists: {
      "Project Features": [
        "Developed a Sudoku Game using Python GUI. Uses an API call to produce a new sudoku board on each attempt.",
        `The user can solve the puzzle, or use the "Solve" option to visualize a Backtracking algorithm automatically solve the puzzle.`,
      ],
    },
    desc: "This is Sudoku game is based on Python's PyGame GUI. It generates a new puzzle using an API call everytime the user clicks 'NewGame'. The application has a Backtracking algorithm that has the dual functionality of checking the user's solution, as well as allowing the user to visualize the Backtracking algorithm automatically solve the puzzle in real time.",
    images: ["/projects/Sudoku/img1.png", "/projects/Sudoku/img2.png"],
    link: "https://github.com/SonuBardai/Azure-RealEst-WebApp",
  },
];
